import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { AuthError } from '@supabase/supabase-js';
import Header from '@/components/Header';
import MinimalFooter from '@/components/MinimalFooter';
import { Brain, Chrome } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';
import { Checkbox } from "@/components/ui/checkbox";

type Profile = Database['public']['Tables']['profiles']['Row'];

export default function SignUp() {
  const navigate = useNavigate();
  const { signUp, signUpWithGoogle } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    betaCode: '',
    acceptTerms: false
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate passwords match
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }

      // Validate terms acceptance
      if (!formData.acceptTerms) {
        toast.error('Please accept the Terms of Service and Privacy Policy');
        return;
      }

      // Check if user already exists
      const { data: existingUser, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', formData.email)
        .single();

      if (existingUser) {
        toast.error('This email is already registered. Please sign in instead.');
        return;
      }

      // Validate beta code
      const { data: isValidCode, error: codeError } = await supabase
        .rpc('validate_beta_code', { code: formData.betaCode });

      if (codeError || !isValidCode) {
        toast.error('Invalid or expired beta code');
        return;
      }

      // Sign up user
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (signUpError) {
        const authError = signUpError as AuthError;
        
        // Handle rate limiting error
        if (authError.message.includes('security purposes')) {
          toast.error('Please wait a moment before trying again. This helps us prevent spam.');
          return;
        }
        
        throw signUpError;
      }

      // Store beta code in localStorage for later use after email verification
      localStorage.setItem('pendingBetaCode', formData.betaCode);

      // Navigate to success page
      navigate('/auth/signup-success');
    } catch (error) {
      console.error('Error during signup:', error);
      const authError = error as AuthError;
      
      // Show more user-friendly error messages
      if (authError.message.includes('User already registered')) {
        toast.error('This email is already registered. Please sign in instead.');
      } else if (authError.message.includes('Invalid email')) {
        toast.error('Please enter a valid email address.');
      } else {
        toast.error('An error occurred during signup. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    if (!formData.betaCode) {
      toast.error('Please enter a beta code to continue');
      return;
    }

    if (!formData.acceptTerms) {
      toast.error('Please accept the Terms of Service and Privacy Policy to continue');
      return;
    }

    setIsLoading(true);
    try {
      await signUpWithGoogle(formData.betaCode);
    } catch (error) {
      console.error('Error during Google signup:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header onStartTour={() => {}} />
      
      <main className="flex-1 relative">
        {/* Background image with gradient overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url("/images/auth-bg.jpg")',
            opacity: 0.1
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 to-background/40" />
        
        {/* Content */}
        <div className="relative container mx-auto px-4 py-12">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Brain className="h-6 w-6 text-primary" />
                </div>
              </div>
              <h1 className="text-3xl font-bold tracking-tight mb-2">Create an Account</h1>
              <p className="text-muted-foreground">
                Join BinauralFlow to unlock premium features and personalized experiences
              </p>
            </div>
            
            <Card className="w-full">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="betaCode">Beta Access Code <span className="text-destructive">*</span></Label>
                    <Input
                      id="betaCode"
                      name="betaCode"
                      type="text"
                      placeholder="Enter your beta access code"
                      value={formData.betaCode}
                      onChange={handleChange}
                      required
                    />
                    <p className="text-sm text-muted-foreground">
                      BinauralFlow is currently in beta. You need a valid access code to create an account.{' '}
                      <a 
                        href="https://binauralflow.ai/signup" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Join the waiting list
                      </a>
                      {' '}to receive a beta access code.
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="acceptTerms"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onCheckedChange={(checked) => 
                        setFormData(prev => ({ ...prev, acceptTerms: checked as boolean }))
                      }
                    />
                    <Label htmlFor="acceptTerms" className="text-sm">
                      I accept the{' '}
                      <a
                        href="https://binauralflow.ai/terms-conditions"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Terms of Service
                      </a>
                      {' '}and{' '}
                      <a
                        href="https://binauralflow.ai/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Privacy Policy
                      </a>
                    </Label>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        continue with
                      </span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    className="w-full"
                    onClick={handleGoogleSignUp}
                    disabled={isLoading || !formData.betaCode}
                  >
                    <Chrome className="mr-2 h-4 w-4" />
                    Sign up with Google
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        Or continue with email
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your.name@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="Create a password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isLoading || !formData.acceptTerms || !formData.email || !formData.password || !formData.confirmPassword || !formData.betaCode}
                  >
                    {isLoading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                  <div className="text-center text-sm">
                    Already have an account?{' '}
                    <Button
                      variant="link"
                      className="p-0 h-auto font-normal"
                      onClick={() => navigate('/auth/signin')}
                    >
                      Sign in
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <MinimalFooter />
    </div>
  );
} 